import type { TuningData } from '../../types/videos';

interface TuningPanelProps {
  tuning: TuningData;
  onToggleSuggestions?: (enabled: boolean) => void;
  onMinSamplesChange?: (value: number) => void;
}

export function TuningPanel({ tuning, onToggleSuggestions, onMinSamplesChange }: TuningPanelProps) {
  const progress = tuning.total > 0 ? (tuning.labelled_count / Math.max(tuning.min_labeled_samples, 1)) * 100 : 0;
  const clampedProgress = Math.min(progress, 100);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">Tuning</h2>
        {tuning.suggestions_enabled ? (
          <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">Suggestions enabled</span>
        ) : (
          <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">Suggestions disabled</span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4 text-center">
        <div className="p-3 bg-gray-50 rounded-lg">
          <div className="text-2xl font-semibold text-gray-900">{tuning.labelled_count}</div>
          <div className="text-xs text-gray-500 uppercase tracking-wide mt-1">Labelled</div>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg">
          <div className="text-2xl font-semibold text-gray-900">{tuning.unlabelled_count}</div>
          <div className="text-xs text-gray-500 uppercase tracking-wide mt-1">Unlabelled</div>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg">
          <div className="text-2xl font-semibold text-gray-900">{tuning.total}</div>
          <div className="text-xs text-gray-500 uppercase tracking-wide mt-1">Total</div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
          <span>Progress toward {tuning.min_labeled_samples} labelled samples</span>
          <span>{Math.round(clampedProgress)}%</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gray-900 transition-all"
            style={{ width: `${clampedProgress}%` }}
          />
        </div>
      </div>

      {tuning.distribution.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-2">Tag distribution</h3>
          <div className="space-y-2">
            {tuning.distribution.map(item => (
              <div key={item.tag} className="flex items-center gap-3">
                <span className="text-xs text-gray-700 w-24 truncate">{item.tag}</span>
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gray-700 rounded-full"
                    style={{ width: `${tuning.total > 0 ? (item.count / tuning.total) * 100 : 0}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500 w-6 text-right">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {onToggleSuggestions && (
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <span className="text-sm text-gray-700">Enable auto-categorisation suggestions</span>
          <button
            onClick={() => onToggleSuggestions(!tuning.suggestions_enabled)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              tuning.suggestions_enabled ? 'bg-gray-900' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                tuning.suggestions_enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      )}

      {onMinSamplesChange && (
        <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
          <label htmlFor="min-samples" className="text-sm text-gray-700">Minimum labelled samples</label>
          <input
            id="min-samples"
            type="number"
            min={0}
            value={tuning.min_labeled_samples}
            onChange={(e) => onMinSamplesChange(Math.max(0, Number(e.target.value)))}
            className="w-20 rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
          />
        </div>
      )}

      {tuning.suggestions_enabled && (
        <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
          Suggestions are enabled but Phase 1 does not generate them yet. Keep labelling videos to prepare for Phase 2.
        </div>
      )}
    </div>
  );
}
